/**
 * City Search Service
 * Handles API calls to the city search endpoint with caching and error handling
 */

import { CityResult, CitySearchResponse, CityAutosuggestError } from '../types/cityAutosuggest.types';

export interface CitySearchParams {
  query: string;
  limit?: number;
  signal?: AbortSignal;
}

export class CitySearchService {
  private baseUrl: string;
  private cache: Map<string, { data: CityResult[]; timestamp: number }>;
  private cacheExpiryMs: number;

  constructor(baseUrl: string = '/api', cacheExpiryMs: number = 60 * 60 * 1000) {
    this.baseUrl = baseUrl;
    this.cache = new Map();
    this.cacheExpiryMs = cacheExpiryMs;
  }

  /**
   * Search for cities with caching and error handling
   */
  async searchCities(params: CitySearchParams): Promise<CityResult[]> {
    const { query, limit = 10, signal } = params;

    // Check cache first
    const cacheKey = `${query}-${limit}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheExpiryMs) {
      return cached.data;
    }

    try {
      const url = new URL(`${this.baseUrl}/cities/search`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('limit', limit.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        throw error;
      }

      const data: CitySearchResponse = await response.json();

      if (!data.success) {
        const error = new Error(data.error?.message || 'Search failed');
        throw error;
      }

      const cities = data.data?.cities || [];

      // Cache the results
      this.cache.set(cacheKey, {
        data: cities,
        timestamp: Date.now(),
      });

      return cities;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw error; // Re-throw abort errors
        }
        
        // Map different error types
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          const networkError = new Error('Network error occurred');
          networkError.name = CityAutosuggestError.NETWORK_ERROR;
          throw networkError;
        }
        
        if (error.message.includes('429') || error.message.includes('quota') || error.message.includes('Too Many Requests')) {
          const quotaError = new Error('API quota exceeded');
          quotaError.name = CityAutosuggestError.QUOTA_EXCEEDED;
          throw quotaError;
        }
        
        if (error.message.includes('400') || error.message.includes('Bad Request')) {
          const validationError = new Error('Invalid search query');
          validationError.name = CityAutosuggestError.INVALID_QUERY;
          throw validationError;
        }
        
        // If it's already an HTTP error, re-throw it
        if (error.message.startsWith('HTTP ')) {
          throw error;
        }
        
        // If it's an API response error, re-throw it
        if (error.message !== 'City search service unavailable') {
          throw error;
        }
      }

      // Generic API error
      const apiError = new Error('City search service unavailable');
      apiError.name = CityAutosuggestError.API_UNAVAILABLE;
      throw apiError;
    }
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache size for debugging
   */
  getCacheSize(): number {
    return this.cache.size;
  }
}

// Default instance
export const citySearchService = new CitySearchService();